<template>
  <div>
    <add-counterparty-form
      v-show="addingCounterparty"
      @cancel="addingCounterparty = false"
      @counterpartyAdded="addingCounterparty = false"
    >
      <template #above-title>
        <back-button
          @click="addingCounterparty = false"
        />
      </template>
    </add-counterparty-form>
    <v-card
      v-show="!addingCounterparty"
      key="upload-document"
    >
      <v-card-title> Upload </v-card-title>
      <v-card-text>
        <v-alert
          v-if="featureEnabled('accountStatus')"
          outlined
          type="info"
        >
          You will be charged <credit-amount :amount="UPLOAD_FOR_SIGNATURE_COST" /> for uploading a document.
        </v-alert>
        <v-alert
          v-if="insufficientCredits && featureEnabled('accountStatus')"
          outlined
          type="error"
        >
          You do not have enough credits to upload a document.
        </v-alert>
        <v-alert
          v-if="isAdmin && account.shortName === 'OpenLaw'"
          :value="true"
          class="white"
          outlined
          type="info"
        >
          You're logged in as an Administrator with 'OpenLaw' as the selected
          account. Go to
          <router-link to="/oladmin/accounts">
            Accounts<!--
            -->
          </router-link>
          to select another account.
        </v-alert>
        <v-form
          v-else-if="!clientUsersLoading"
          ref="uploadFileForm"
          v-model="docDetailsValid"
        >
          <file-input
            label="File*"
            required
            :accept="uploadAcceptExtensions"
            :rules="[
              validateRequiredFile,
              validateFileMimeType
            ]"
            @change="(files) => filesChange('add', files)"
          />
          <!-- TODO readonly and disabled should be if not New doc. -->

          <v-text-field
            ref="docName"
            v-model="docName"
            :rules="[validateRequired]"
            label="Document Name*"
            required
            @blur="docNameDirty = true"
          />

          <document-type-select
            ref="documentTypeSelect"
            v-model="documentType"
          />

          <v-row align="baseline">
            <v-col>
              <signatory-select
                :selected-account="firstPartyAccountId"
                :selected-signatories="firstPartySignatories"
                @change="selectFirstParty"
              />
            </v-col>
          </v-row>
          <v-row align="baseline">
            <v-col cols="8">
              <v-autocomplete
                v-model="selectedCounterParties"
                multiple
                chips
                deletable-chips
                return-object
                :items="counterpartyUsersSelect"
                :filter="filterCounterpartyList"
                :rules="[validateRequired]"
                item-text="name"
                item-value="id"
                label="Counter Parties*"
                required
              >
                <template #item="{ item }">
                  <v-list-item-content
                    dense
                  >
                    <v-list-item-title>
                      {{ counterpartyName(item) }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-spacer />
            <v-col>
              <v-btn
                color="primary"
                outlined
                @click="showAddCounterparty()"
              >
                Add new
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="notifyOnCompletion"
                hint="Is there anyone else you wish to notify once this document is complete? Enter their email address."
                label="Notify on completion"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="uploading"
          small
          text
          @click="cancelHandler"
        >
          Cancel
        </v-btn>
        <v-btn
          :disabled="!canUpload"
          :loading="uploading"
          small
          color="primary"
          @click="uploadHandler()"
        >
          Upload
        </v-btn>
        <capture-asset-data-dialog
          v-if="showCaptureAssetDialog"
          :show-dialog="showCaptureAssetDialog"
          :doc-data="documentData"
          @cancelClick="showCaptureAssetDialog = false"
        />
        <prepare-document-dialog
          :show-dialog="showPrepareDocumentDialog"
          :doc-data="documentData"
          @done="captureAssetHandler()"
        />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { fsdb } from '@/firebase'
import orderBy from 'lodash/orderBy'
import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import startCase from 'lodash/startCase'
import { defaultFilter } from 'vuetify/lib/util/helpers'
import { isSingleUserAccount, isStandardBusinessAccount } from '@/dataAccess/accountTypes'
import SignatorySelect from './SignatorySelect.vue'
import BackButton from '@/components/BackButton.vue'
import DocumentTypeSelect from '@/components/DocumentTypeSelect.vue'
import CreditAmount from '@/components/CreditAmount.vue'
import { createCounterPartiesData } from '@/dataAccess/parties'
import { uploadAcceptExtensions } from '@/utils/mimeTypes'
import { validateRequired, validateRequiredFile, validateFileMimeType } from '@/utils/validationRules'

const _ = { isEmpty, orderBy, groupBy, startCase }

const FileInput = () => import('@/components/utils/OlDragAndDropFileInput')
const CaptureAssetDataDialog = () => import('@/components/CaptureAssetDataDialog')
const PrepareDocumentDialog = () => import('@/components/PrepareDocumentDialog')
const AddCounterpartyForm = () => import('@/components/AddCounterpartyForm')

const UPLOAD_FOR_SIGNATURE_COST = 1

export default {
  name: 'DocumentUpload',
  components: {
    AddCounterpartyForm,
    BackButton,
    CaptureAssetDataDialog,
    CreditAmount,
    PrepareDocumentDialog,
    DocumentTypeSelect,
    FileInput,
    SignatorySelect
  },
  data: function () {
    return {
      uploadAcceptExtensions,
      docName: null,
      client: null,
      uploading: false,
      jobsLoading: false,
      clientUsersLoading: false,
      counterpartyUsersLoading: false,
      accountUsersLoading: false,
      docDetailsValid: false,
      uploadProgress: 50,
      files: {},
      firstPartyAccountId: null,
      firstPartySignatories: [],
      showCaptureAssetDialog: false,
      showPrepareDocumentDialog: false,
      documentData: {},
      addingCounterparty: false,
      saving: false,
      selectedCounterParties: [],
      documentType: null,
      notifyOnCompletion: null,
      UPLOAD_FOR_SIGNATURE_COST
    }
  },
  computed: {
    canUpload() {
      return this.docDetailsValid && !this.insufficientCredits
    },
    clientUsersMapped() {
      return _.orderBy(this.clientUsers.map(client => ({
        ...client,
        initials: client.initials || client.name.split(' ').reduce((response, word) => { response += word.slice(0, 1); return response }, '')
      })), 'name')
    },
    clientUsersMappedById() {
      return Object.fromEntries(this.clientUsersMapped.map(user => [user.id, user]))
    },
    clientUsersSelect() {
      if (this.clientUsersLoading) { return [] }

      const clientUsersSimple = this.clientUsers.map(({ name, id, email, ...rest }) => ({ name, id, email }))
      return _.orderBy(clientUsersSimple, ['name', 'email'])
    },
    counterpartyUsersSelect() {
      if (this.counterpartyUsersLoading) { return [] }

      return _.orderBy(this.counterpartyUsers, ['name', 'email'])
    },
    insufficientCredits() {
      return this.accountStatus.credits < UPLOAD_FOR_SIGNATURE_COST
    },
    jobsArray() {
      if (this.jobsLoading) { return [] }
      const jobsArray = Object.entries(this.jobs)
        .filter(([jobId, job]) => jobId !== '.key')
        .map(([jobId, job]) => {
          const clientDetails = _.orderBy(job.clients.map(clientId => this.clientUsersMappedById[clientId]), 'name')

          return { jobId: jobId, ...job, accountId: this.account.id, clientDetails: clientDetails }
        })

      return _.orderBy(jobsArray, 'jobName')
    },
    jobsSelect() {
      let jobs = []

      if (this.client) {
        const newJob = {
          accountId: this.$store.state.account.id,
          clients: [this.client]
        }

        newJob.clientDetails = _.orderBy(newJob.clients.map(clientId => this.clientUsersMappedById[clientId]), 'name')

        // create jobId from client(s) initials and yyyy-mm
        // newJob.jobId = newJob.clientDetails.map(user => user.initials).join('/') + '/' + this.$toLocaleYYYYMMDDsH_MMpString(new Date()).slice(0, 7)
        // create jobName from client(s) last name (last word in name) and template name
        // newJob.jobName = Array.from(new Set(newJob.clientDetails.map(user => user.name.split(' ').pop()))).join(', ') + ' ' + (this.selectedTemplateObjects.length > 0 ? this.selectedTemplateObjects[0].name : '<select\xa0template>')
        newJob.jobId = 'xx/xxxx-xx'
        newJob.jobName = 'New Job'

        jobs.push(newJob)
        jobs = jobs.concat(...this.jobsArray.filter(job => job.clients.includes(this.client)))
      } else {
        jobs.push({ jobName: 'New Job', jobId: 'xx/xxxx-xx', clientDetails: [{ name: 'No client(s) selected.' }], disabled: true })
        jobs = jobs.concat(...this.jobsArray)
      }

      return jobs
    },
    isSingleUser() {
      return isSingleUserAccount(this.account.type)
    }
  },
  watch: {
    account: {
      handler: function (newVal, oldVal) {
        if ('id' in newVal) {
          this.jobsLoading = true
          this.$binding('jobs', fsdb.collection('accountJobs').doc(this.account.id))
            .then(() => { this.jobsLoading = false })
            .catch(err => {
              if (err.message.includes('permission denied')) {
                this.jobsLoading = false
                // just means account doesn't have jobs yet
                return
              }
              // TODO sentry log
              return err
            })
        }
      },
      deep: true,
      immediate: true
    },
    client: function (newVal) {
      if (newVal) {
        if (!this.job) { return }
        if (this.jobs[this.job] && this.jobs[this.job].clients && this.jobs[this.job].clients.includes(this.client)) { return }
        this.job = null
      }
    },
    job: function (newVal) {
      if (newVal) {
        if (this.client) { return }
        if (this.jobs[this.job] && this.jobs[this.job].clients && this.jobs[this.job].clients.length === 1) {
          this.client = this.jobs[this.job].clients[0]
        }
      }
    }
  },
  beforeCreate() {
    this.dispatchActions(['checkAuth', 'get_documents', 'get_account', 'get_accountStatus'])

    this.mapStates(['user', 'appUserBinding', 'clientUsers', 'account', 'counterpartyUsers', 'appUser', 'accountStatus', 'accountStatusBinding'])
    this.mapGetters(['isIndividual', 'isAdmin', 'featureEnabled'])

    this._ = _
    this.defaultFilter = defaultFilter

    this.$store.dispatch('get_appUser').then(() => {
      this.firstPartySignatories = [{
        id: this.appUser.id,
        name: this.appUser.name
      }]
      this.firstPartyAccountId = this.appUser.accountId
    })
  },
  async created() {
    await this.appUserBinding
    await this.accountStatusBinding

    if (this.isIndividual) {
      this.clientUsersLoading = false
      this.accountUsersLoading = false
    } else if (this.isSingleUser) {
      this.counterpartyUsersLoading = true
      this.$store.dispatch('get_counterpartyUsers').then(() => { this.counterpartyUsersLoading = false })
    } else {
      this.$store.dispatch('get_clientUsers').then(value => { this.clientUsersLoading = false })
      this.$store.dispatch('get_accountUsers').then(value => {
        /* TODO BETA workaround for firestore bug where it fires onSnapshot twice with different data
           check if this still occurs after next firestore release */
        setTimeout(() => { this.accountUsersLoading = false }, 500)
      })
    }
  },
  methods: {
    validateRequired,
    validateRequiredFile,
    validateFileMimeType,
    counterpartyName: function ({ name, accountShortName, accountType }) {
      if (isStandardBusinessAccount(accountType)) {
        return `${name} (${accountShortName})`
      }
      return name
    },
    showAddCounterparty: function () {
      this.addingCounterparty = true
    },
    filterJobList(item, queryText) {
      // copied from Vuetify/lib/utils/helpers
      return Object.keys(item).some(key => this.defaultFilter(item[key], queryText, item))
    },
    filterClientList(item, queryText) {
      // copied from Vuetify/lib/utils/helpers
      return Object.keys(item).some(key => this.defaultFilter(item[key], queryText, item))
    },
    filterCounterpartyList(item, queryText) {
      // copied from Vuetify/lib/utils/helpers
      return Object.keys(item).some(key => this.defaultFilter(item[key], queryText, item))
    },
    cancelHandler() {
      this.$refs.uploadFileForm.reset()
      this.$emit('close')
    },
    uploadHandler() {
      this.uploadToStorage()
    },
    captureAssetHandler() {
      this.showPrepareDocumentDialog = false
      this.showCaptureAssetDialog = true
    },
    prepareDocHandler() {
      this.showPrepareDocumentDialog = true
    },
    async uploadToStorage() {
      this.uploading = true

      // TODO fix mixed single-file / multi-file logic
      const firstParty = {
        accountId: this.firstPartyAccountId,
        signatoryIds: this.firstPartySignatories.map(({ id }) => id)
      }
      const counterParties = createCounterPartiesData(this.selectedCounterParties)
      const notifyOnCompletion = this.notifyOnCompletion ? [this.notifyOnCompletion] : []
      const documentData = {
        firstParty,
        counterParties,
        documentType: this.documentType,
        name: this.docName,
        notifyOnCompletion
      }

      await this.$handlingError(`Error uploading document "${this.docName}"`, async () => {
        const {
          data: {
            documentId
          }
        } = await this.$api.uploadUnsignedDocument(this.files[0], documentData)

        this.documentData = { documentId, ...documentData }

        this.prepareDocHandler()
        this.$showSuccessbar('Document uploaded.')
        this.cancelHandler()
      }, { final: () => { this.uploading = false } })
    },
    filesChange(document, files) {
      // if docName is blank, give it the same name the file, minus the extension
      if (document === 'add' && !this.docName && files && files.length) {
        this.docName = this.stripExtension(files[0].name)
      }

      this.files = files
    },
    stripExtension(filename) {
      if (!filename) { return '' }
      return filename.replace(/\.[^/.]+$/, '')
    },
    selectFirstParty({ accountId, signatories }) {
      this.firstPartyAccountId = accountId
      this.firstPartySignatories = signatories
    }
  }
}
</script>

<style lang="scss" scoped>
.v-btn.v-size--default.back-button {
  padding-left: 0;
}
</style>
